/**
 * The legal pages' text (launch redesign 2a/2b, Olivia 14 Oct): Markdown files in
 * src/content/legal, `<kind>.<lang>.md`, each opening with a small front-matter block.
 * English is the binding version and the fallback for a language that has no file.
 */

export type LegalKind = "terms" | "privacy";
export const LEGAL_KINDS: readonly LegalKind[] = ["terms", "privacy"];

export interface LegalClause {
  /** The anchor: `clause-<n>` from the heading's number, so links stay put when wording changes. */
  id: string;
  title: string;
}

export interface LegalDoc {
  kind: LegalKind;
  /** The language actually shown (English when the asked-for one has no file). */
  lang: string;
  title: string;
  version: string;
  effective: string;
  updated: string;
  /** Under a translation's title: which version is binding. Absent in English. */
  binding?: string;
  /** "In short": at most three bullets, labelled as a summary. */
  short: string[];
  body: string;
  clauses: LegalClause[];
}

const FILES = import.meta.glob("/src/content/legal/*.md", { query: "?raw", import: "default", eager: true }) as Record<string, string>;

/** `---\nkey: value\n---\nbody` → the keys and the body. Values are single lines. */
export function parseFrontMatter(raw: string): { meta: Record<string, string>; body: string } {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(raw);
  if (!m) return { meta: {}, body: raw };
  const meta: Record<string, string> = {};
  for (const line of m[1].split(/\r?\n/)) {
    const i = line.indexOf(":");
    if (i > 0) meta[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  return { meta, body: m[2] };
}

/** "1. Who we are" → `clause-1`; a heading without a number gets a slug. */
export function clauseId(heading: string): string {
  const n = /^(\d+(?:\.\d+)*)[.)]?\s/.exec(heading.trim());
  if (n) return `clause-${n[1].replace(/\./g, "-")}`;
  return heading.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export function legalDoc(kind: LegalKind, language: string, files: Record<string, string> = FILES): LegalDoc {
  const base = (language || "en").toLowerCase().split("-")[0];
  const lang = files[`/src/content/legal/${kind}.${base}.md`] !== undefined ? base : "en";
  const { meta, body } = parseFrontMatter(files[`/src/content/legal/${kind}.${lang}.md`] ?? "");
  const clauses = Array.from(body.matchAll(/^##\s+(.+)$/gm), (h) => ({ id: clauseId(h[1]), title: h[1].trim() }));
  return {
    kind,
    lang,
    title: meta.title ?? kind,
    version: meta.version ?? "1",
    effective: meta.effective ?? "",
    updated: meta.updated ?? "",
    binding: lang === "en" ? undefined : meta.binding,
    short: (meta.short ?? "").split("|").map((s) => s.trim()).filter(Boolean).slice(0, 3),
    body,
    clauses,
  };
}

/**
 * The legal version an account accepts (launch redesign 2c): the higher of the Terms' and the
 * Privacy Policy's versions, effective on the later of their dates. The API keeps the last one
 * each account accepted (`/api/me/legal`); a higher one here shows the notice once.
 */
export function currentLegal(files: Record<string, string> = FILES): { version: number; effective: string } {
  const docs = LEGAL_KINDS.map((k) => legalDoc(k, "en", files));
  return {
    version: Math.max(...docs.map((d) => Number(d.version) || 1)),
    effective: docs.map((d) => d.effective).filter(Boolean).sort().at(-1) ?? "",
  };
}

const PENDING_KEY = "lilia.legal.accept-pending";

/** Sign-up with the terms box ticked: remember the version until the account exists and it can be saved. */
export function stashLegalAcceptance(version = currentLegal().version) {
  try { window.localStorage.setItem(PENDING_KEY, String(version)); } catch { /* private window: the notice asks later */ }
}

/** The version accepted at sign-up and not yet saved, if any. */
export function pendingLegalAcceptance(): number | null {
  try {
    const v = Number(window.localStorage.getItem(PENDING_KEY));
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch { return null; }
}

export function clearPendingLegalAcceptance() {
  try { window.localStorage.removeItem(PENDING_KEY); } catch { /* nothing to clear */ }
}
